import { ConfirmDialog } from '@h2hspark/ui';
import * as React from 'react';

import {
  DISCONNECT_CONFIRMATION,
  DISCONNECT_CONFIRM_LABEL,
  DISCONNECT_KEEP_LABEL,
  disconnectTitle,
} from './constants';

/**
 * The disconnect confirmation (`MEM-034` AC-1, AC-2).
 *
 * ## Transcribed from `Member App.dc.html`, the disconnect sheet
 *
 * The prototype draws a bottom sheet with a grab handle, a `--danger-bg` icon tile beside the
 * question, the consequence as a paragraph, and two pill buttons — *Keep connection* on the left
 * and a destructive *Disconnect* on the right. `ConfirmDialog` from `packages/ui` is the shared
 * component for exactly this shape (a title, a description, a cancel label, a destructive confirm
 * label and a busy state), and `building-from-designs.md` says to reach for the primitive before
 * writing the markup. The prototype's *Disconnected* success sheet is not built: the screen
 * navigates back to a list that no longer contains them, which says the same thing without a
 * second dismissal.
 *
 * ## The copy is a deliberate deviation, and it is the one worth reading
 *
 * The prototype's sentence is *"They'll be removed from your connections and **your private notes
 * will be deleted. This can't be undone**, and they won't be notified."*
 *
 * Two of those three clauses are false of what was built. `connection.ts` makes removal a status
 * rather than a delete precisely so `MEM-034` AC-2 and AC-3 can both hold — *"a delete would
 * satisfy AC-2 and destroy the evidence AC-3 protects"* — and neither member's note is written.
 * A member could act on the prototype's promise: somebody who wants a note gone would disconnect
 * to achieve it, and it would not be gone.
 *
 * `building-from-designs.md`: the specification wins on behaviour, the prototype wins on
 * presentation, and the disagreement is raised rather than resolved in silence. The layout is the
 * prototype's; the sentence is `constants.ts`'s `DISCONNECT_CONFIRMATION`, and it keeps the
 * clause that is exactly right — nobody is told.
 */
export function DisconnectDialog({
  open,
  firstName,
  busy,
  onConfirm,
  onOpenChange,
}: {
  readonly open: boolean;
  readonly firstName: string;
  readonly busy: boolean;
  readonly onConfirm: () => void;
  readonly onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  return (
    <ConfirmDialog
      open={open}
      title={disconnectTitle(firstName)}
      description={DISCONNECT_CONFIRMATION}
      cancelLabel={DISCONNECT_KEEP_LABEL}
      confirmLabel={DISCONNECT_CONFIRM_LABEL}
      destructive
      busy={busy}
      onConfirm={onConfirm}
      onOpenChange={onOpenChange}
    />
  );
}
